import { useQuery } from "@tanstack/react-query";

import type { CatalogSummaryInput } from "hooks/useCatalogSummary";
import { getPlatform } from "api/methods";
import { useCatalogLocationId } from "hooks/useCatalogLocationId";
import { useMyProductsVisibility } from "hooks/useMyProductsVisibility";
import { useSkinDropVisibility } from "hooks/useSkinDropVisibility";
import { useUser } from "modules/user/useUser/useUser";

/**
 * The tenancy, location and retail-visibility answers every catalog query filters on, the same ones
 * `useCatalogSummary` waits for; undefined until all of them are in.
 */
export const useCatalogFilterInput = (): CatalogSummaryInput | undefined => {
  const { data: platform } = useQuery({ ...getPlatform.keyed() });
  const { user } = useUser();
  const { isSkinDropVisibleOnMarketplace, isSkinDropLoading } = useSkinDropVisibility();
  const { isMyProductsVisibleOnMarketplace, isMyProductsLoading } = useMyProductsVisibility();
  const { platformLocationId, isReady: isLocationReady } = useCatalogLocationId(user);

  if (!platform?.id || !user || !isLocationReady || isSkinDropLoading || isMyProductsLoading) {
    return undefined;
  }

  return {
    platformId: platform.id,
    platformLocationId,
    visibility: {
      areRetailProductsVisible: isMyProductsVisibleOnMarketplace,
      isSkinDropVisible: isSkinDropVisibleOnMarketplace,
    },
  };
};

export const catalogFilterInputKey = (input: CatalogSummaryInput | undefined) => [
  input?.platformId ?? null,
  input?.platformLocationId ?? null,
  !!input?.visibility.areRetailProductsVisible,
  !!input?.visibility.isSkinDropVisible,
];
